/**
 * 92. Entity Builder — create custom entities; fields; options; validation;
 * activation; deactivation policy. Custom entities are JSONB-backed; there is
 * no runtime DDL. Example: Vehicle Inspection with typed fields.
 *
 * Arabic and English (C7): every word from the catalogs, the field types
 * included. An entity name is an identifier in Latin letters by rule, so the
 * example name is isolated left to right; the name someone types is quoted
 * back as typed.
 *
 * Validate & activate had no handler: enabled once the typed name passed
 * the checks, it then did nothing (found by test/no-dead-buttons.test.ts,
 * UI-HONEST). Custom objects are configurability CF-17, after the launch
 * (configurability.md section 9.3), so it is shown disabled with that
 * reason; the name check still says what a name may be.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  Badge,
  DataTable,
  Field,
  Input,
  BlockedActionExplanation,
  type BlockReason,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle, ltr } from "@/lib/i18n/format";
import { valueLabel } from "@/lib/i18n/records";
import { formatQuantity } from "@/lib/money";

interface EntityDefinition {
  id: string;
  name: string;
  module: string;
  storageType: "Relational" | "Jsonb";
  status:
    "Draft" | "Validated" | "Simulated" | "Approved" | "Active" | "Superseded";
  version: number;
  fieldCount: number;
}

const RESERVED_NAMES = [
  "Journal",
  "Ledger",
  "Audit",
  "Tenant",
  "User",
  "Permission",
  "Role",
  "ConfigContext",
  "Event",
  "Outbox",
];

/** The database's field_data_type values, in the groups the card lists. */
const FIELD_TYPE_GROUPS = [
  ["Text", "LongText", "RichText", "Number", "Currency", "Percentage"],
  ["Date", "DateTime", "Boolean", "Select", "MultiSelect"],
  ["Relation", "User", "Employee", "Company", "Attachment"],
  ["Formula", "JSON"],
];

const EXAMPLE_NAME = "VehicleInspection";

export function EntityBuilderPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<EntityDefinition>(
    ["config", "entities"],
    "/config/entities",
  );
  const [draftName, setDraftName] = useState("");
  const [blocked, setBlocked] = useState<BlockReason[]>([]);

  function checkDraftName(name: string) {
    setDraftName(name);
    const reasons: BlockReason[] = [];
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
      reasons.push({
        code: "INVALID_NAME",
        message: t("admin.entityBuilder.invalidName"),
      });
    }
    if (RESERVED_NAMES.includes(name)) {
      reasons.push({
        code: "RESERVED_NAME",
        message: t("admin.entityBuilder.reservedName", { name }),
      });
    }
    setBlocked(reasons);
  }

  return (
    <div>
      <PageHeader
        title={t("admin.entityBuilder.title")}
        subtitle={t("admin.entityBuilder.subtitle")}
        actions={
          <PendingButton variant="primary" reason="afterLaunch" slice="CF-17">
            {t("admin.entityBuilder.validateActivate")}
          </PendingButton>
        }
      />
      <div className="atlas-grid atlas-grid--2 mb-4">
        <Card title={t("admin.entityBuilder.newEntity")}>
          <Field
            label={t("admin.entityBuilder.entityName")}
            required
            hint={t("admin.entityBuilder.entityNameHint", {
              example: ltr(EXAMPLE_NAME),
            })}
          >
            <Input
              value={draftName}
              onChange={(e) => checkDraftName(e.target.value)}
              placeholder={ltr(EXAMPLE_NAME)}
              dir="ltr"
            />
          </Field>
          {blocked.length > 0 && (
            <BlockedActionExplanation
              title={t("admin.entityBuilder.whyBlocked")}
              reasons={blocked}
            />
          )}
        </Card>
        <Card title={t("admin.entityBuilder.fieldTypes")}>
          <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 14 }}>
            {FIELD_TYPE_GROUPS.map((group) => (
              <li key={group[0]}>
                {group
                  .map((type) => valueLabel(t, "fieldType", type))
                  .join(t("shared.listSeparator"))}
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <Card title={listTitle(t("admin.entityBuilder.list"), data?.total ?? 0)}>
        {isLoading || !data ? (
          <Skeleton rows={5} />
        ) : (
          <DataTable<EntityDefinition>
            rows={data.items}
            columns={[
              { key: "name", label: t("fields.entity") },
              { key: "module", label: t("fields.module") },
              {
                key: "storageType",
                label: t("admin.entityBuilder.storage"),
                render: (r) => (
                  <Badge
                    tone={r.storageType === "Jsonb" ? "primary" : "default"}
                  >
                    {valueLabel(t, "storageType", r.storageType)}
                  </Badge>
                ),
              },
              {
                key: "fieldCount",
                label: t("admin.entityBuilder.fields"),
                numeric: true,
                render: (r) => formatQuantity(r.fieldCount, 0),
              },
              {
                key: "version",
                label: t("fields.version"),
                numeric: true,
                render: (r) => formatQuantity(r.version, 0),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="EntityDefinition" />
                ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
}
